import { APPS } from "@/constants/appNavigation";
import {
  DelegationPermission,
  canAccessAsDelegate,
} from "@/constants/delegationPermissions";
import {
  VIRKSOMHEDSBLANKET_LEGACY_PATH,
  VIRKSOMHEDSBLANKET_PATH,
} from "@/constants/routes";

export const DELEGATED_ROUTE_RULES: {
  prefix: string;
  permission: DelegationPermission;
}[] = [
  {
    prefix: VIRKSOMHEDSBLANKET_PATH,
    permission: DelegationPermission.Selskabsblanketten,
  },
  {
    // Legacy-stien SKAL stå her. canAccessDelegatedPath returnerer false når ingen regel
    // matcher, så uden denne ville en fuldmægtig med et gammelt bogmærke blive afvist før
    // redirectet nåede at køre.
    prefix: VIRKSOMHEDSBLANKET_LEGACY_PATH,
    permission: DelegationPermission.Selskabsblanketten,
  },
  {
    prefix: "/efteruddannelse",
    permission: DelegationPermission.Efteruddannelse,
  },
];

export const ALWAYS_ALLOWED_DELEGATED_PREFIXES = ["/Home/"];

export type DelegatedRouteUserInfo = {
  id?: string | null;
  isDelegatedSession?: boolean;
  delegationPermissions?: number | null;
  identityType?: "Personal" | "Company" | "Accountant";
};

/**
 * Stierne til de apps, der dækker en personlig forpligtelse. Udledes af `APPS`, så markøren
 * står ét sted — en ny personlig app skal ikke huskes to gange.
 */
const PERSONAL_IDENTITY_PREFIXES = APPS.filter(
  (app) => app.requiresPersonalIdentity,
).map((app) => app.basePath);

const matchesPathPrefix = (pathname: string, prefix: string) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export const canAccessDelegatedPath = (
  pathname: string,
  userInfo: DelegatedRouteUserInfo | null | undefined,
): boolean => {
  if (!userInfo) {
    return true;
  }

  // Før den almindelige self-session-genvej nedenfor: en firma- eller revisor-session ER en
  // self session og slap derfor igennem til efteruddannelse, hvor hvert API-kald nu svarer 403.
  // Menupunktet er allerede skjult (se getVisibleApps), men et bogmærke gik uden om det.
  //
  // Fail-open når identityType mangler, som i getVisibleApps: værnet mod forkert ejerskab
  // ligger i CLE-API'et, og et svar uden feltet må ikke spærre en advokat ude af sin egen side.
  if (
    userInfo.identityType !== undefined &&
    userInfo.identityType !== "Personal" &&
    PERSONAL_IDENTITY_PREFIXES.some((prefix) =>
      matchesPathPrefix(pathname, prefix),
    )
  ) {
    return false;
  }

  if (!userInfo.isDelegatedSession && userInfo.id) {
    return true;
  }

  if (!userInfo.id) {
    return pathname === "/";
  }

  if (pathname === "/") {
    return true;
  }

  if (
    ALWAYS_ALLOWED_DELEGATED_PREFIXES.some((prefix) =>
      pathname.startsWith(prefix),
    )
  ) {
    return true;
  }

  const rule = DELEGATED_ROUTE_RULES.find(({ prefix }) =>
    matchesPathPrefix(pathname, prefix),
  );

  if (rule) {
    return canAccessAsDelegate(userInfo, rule.permission);
  }

  return false;
};
